import { AppLayout } from "@/components/layout/AppLayout";
import { StatCard } from "@/components/dashboard/StatCard";
import { RecentActivity } from "@/components/dashboard/RecentActivity";
import { WeeklyProgress } from "@/components/dashboard/WeeklyProgress";
import { useMemo, useState } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { PageStateGate } from "@/components/states";
import { StaggerContainer, StaggerItem, PageTransition } from "@/components/ui/page-transition";
import { Compass, ArrowRight } from "@phosphor-icons/react";
import {
  FileText,
  ChatCircle,
  Crosshair,
  Student,
  VideoCamera,
  Trophy,
  Flame,
  UserFocus,
  ChartLineUp,
  ChatCircleDots,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import {
  filterActivityByPeriod,
  type ActivityKind,
  type DashboardPeriod,
  type WeeklyBucket,
  useActivityFeed,
} from "@/features/dashboard/hooks/useActivityFeed";
import { pointsFor } from "@/features/dashboard/constants/activityPoints";
import { formatGreetingName, stripHonorificPrefix } from "@/lib/profileName";
import { UsageMiniCard } from "@/features/uso/components/UsageMiniCard";

const PERIOD_OPTIONS: Array<{ value: DashboardPeriod; label: string }> = [
  { value: "today", label: "Hoje" },
  { value: "7d", label: "7 dias" },
  { value: "14d", label: "14 dias" },
  { value: "30d", label: "30 dias" },
  { value: "3m", label: "3 meses" },
  { value: "all", label: "Todo período" },
];

function buildPeriodBuckets(
  period: DashboardPeriod,
  items: { createdAt: string; kind: ActivityKind }[],
): WeeklyBucket[] {
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  if (period === "today") {
    const pts = items.reduce((sum, it) => sum + pointsFor(it.kind), 0);
    return [{ day: "Hoje", count: items.length, points: pts }];
  }

  if (period === "7d" || period === "14d") {
    const days = period === "7d" ? 7 : 14;
    const labels = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    const buckets: WeeklyBucket[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const dayItems = items.filter((it) => {
        const t = new Date(it.createdAt);
        const k = `${t.getFullYear()}-${t.getMonth()}-${t.getDate()}`;
        return k === key;
      });
      const pts = dayItems.reduce((sum, it) => sum + pointsFor(it.kind), 0);
      const dayLabel = period === "7d" ? labels[d.getDay()] : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;
      buckets.push({ day: dayLabel, count: dayItems.length, points: pts });
    }
    return buckets;
  }

  if (period === "30d" || period === "3m") {
    const groupSize = period === "30d" ? 3 : 7;
    const totalDays = period === "30d" ? 30 : 90;
    const buckets: WeeklyBucket[] = [];
    for (let startOffset = totalDays - groupSize; startOffset >= 0; startOffset -= groupSize) {
      const start = new Date(today);
      start.setDate(start.getDate() - startOffset);
      const end = new Date(start);
      end.setDate(end.getDate() + groupSize - 1);
      const rangeItems = items.filter((it) => {
        const t = new Date(it.createdAt).getTime();
        return t >= start.getTime() && t <= end.getTime() + 86399999;
      });
      const pts = rangeItems.reduce((sum, it) => sum + pointsFor(it.kind), 0);
      const label =
        period === "30d"
          ? `${start.getDate()}-${end.getDate()}`
          : `${start.getDate()}/${start.getMonth() + 1}`;
      buckets.push({ day: label, count: rangeItems.length, points: pts });
    }
    return buckets;
  }

  // all: buckets mensais
  const byMonth = new Map<string, { count: number; pts: number }>();
  items.forEach((it) => {
    const d = new Date(it.createdAt);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const cur = byMonth.get(key) ?? { count: 0, pts: 0 };
    byMonth.set(key, { count: cur.count + 1, pts: cur.pts + pointsFor(it.kind) });
  });
  return Array.from(byMonth.entries())
    .sort((a, b) => (a[0] > b[0] ? 1 : -1))
    .slice(-12)
    .map(([key, { count, pts }]) => {
      const [year, month] = key.split("-").map(Number);
      const d = new Date(year, month, 1);
      const label = d.toLocaleDateString("pt-BR", { month: "short" });
      return { day: label, count, points: pts };
    });
}

// First-run cards — shown when user has zero activity AND incomplete profile.
// Each card links to a feature with concrete value preview.
const FIRST_RUN_CARDS: Array<{
  title: string;
  description: string;
  to: string;
  icon: typeof UserFocus;
}> = [
  {
    title: "Decodifique seu primeiro cliente",
    description:
      "Identifique tipo de Eneagrama em 2 minutos. Sem cadastro de paciente.",
    to: "/decodificar",
    icon: UserFocus,
  },
  {
    title: "Analise sua primeira consulta",
    description: "Suba um áudio ou cole transcrição. Receba insights da IA.",
    to: "/consultas",
    icon: ChartLineUp,
  },
  {
    title: "Gere seu primeiro roteiro",
    description: "Reels ou Stories baseados no seu posicionamento.",
    to: "/roteiros",
    icon: ChatCircleDots,
  },
];

export default function Index() {
  usePageTitle("Dashboard");
  const { user } = useCurrentUser();
  const { query: businessContextQuery, completenessPct } = useBusinessContext();
  const [period, setPeriod] = useState<DashboardPeriod>("7d");
  const { items: activityItems, isLoading: activityLoading } = useActivityFeed(
    period === "all" ? 5000 : 800,
  );
  const firstName = businessContextQuery.data
    ? formatGreetingName(
        businessContextQuery.data.display_name,
        businessContextQuery.data.honorific_prefix,
      )
    : stripHonorificPrefix(user?.name ?? "").split(/\s+/).filter(Boolean)[0] || "Visitante";

  const filteredItems = useMemo(
    () => filterActivityByPeriod(activityItems, period),
    [activityItems, period],
  );

  const kindCount = useMemo(() => {
    const acc: Record<ActivityKind, number> = {
      consulta: 0,
      decodificar: 0,
      criativo: 0,
      roteiro: 0,
      simulador: 0,
    };
    filteredItems.forEach((item) => {
      acc[item.kind] += 1;
    });
    return acc;
  }, [filteredItems]);

  const { periodPoints, streakDays } = useMemo(() => {
    const now = new Date();
    const dayKey = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate(),
      ).padStart(2, "0")}`;

    const activityByDay = new Set(
      activityItems.map((item) => dayKey(new Date(item.createdAt))),
    );
    const periodPoints = filteredItems.reduce((sum, it) => sum + pointsFor(it.kind), 0);

    // Regra solicitada:
    // - se hoje não teve atividade => streak = 0
    // - se teve hoje => conta dias consecutivos para trás
    let streak = 0;
    const cursor = new Date(now);
    cursor.setHours(0, 0, 0, 0);

    while (activityByDay.has(dayKey(cursor))) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }

    return { periodPoints, streakDays: streak };
  }, [activityItems, filteredItems]);

  const buckets = useMemo(() => buildPeriodBuckets(period, filteredItems), [period, filteredItems]);

  // First-run detection: incomplete profile + zero activity (any kind, any period).
  // We rely on the existing `activityItems` aggregate — it already covers
  // consulta/roteiro/criativo/decodificar/simulador. No new endpoints needed.
  const isFirstRun =
    !activityLoading &&
    !businessContextQuery.isLoading &&
    completenessPct < 100 &&
    activityItems.length === 0;

  type StatTile = { title: string; value: number | string; subtitle: string; icon: typeof FileText };
  const stats: StatTile[] = [
    {
      title: "Roteiros Gerados",
      value: kindCount.roteiro,
      subtitle: "No período",
      icon: FileText,
    },
    {
      title: "Simulações Feitas",
      value: kindCount.simulador,
      subtitle: "No período",
      icon: Student,
    },
    {
      title: "Consultas Analisadas",
      value: kindCount.consulta,
      subtitle: "No período",
      icon: ChatCircle,
    },
    {
      title: "Criativos Analisados",
      value: kindCount.criativo,
      subtitle: "No período",
      icon: VideoCamera,
    },
    {
      title: "Pacientes Decodificados",
      value: kindCount.decodificar,
      subtitle: "No período",
      icon: Crosshair,
    },
    {
      title: "Streak Atual",
      value: `${streakDays} dias`,
      subtitle: "Atualizado em tempo real",
      icon: Flame,
    },
    {
      title: "Pontuação",
      value: periodPoints,
      subtitle: periodPoints > 0 ? "Pontos no período" : "Sem pontuação",
      icon: Trophy,
    },
  ];

  return (
    <AppLayout>
      <PageStateGate
        empty={{
          icon: <Compass weight="duotone" className="h-6 w-6" aria-hidden="true" />,
          title: "Bem-vindo",
          description: "Seu hub ainda está vazio — comece explorando os módulos ao lado.",
        }}
      >
        <PageTransition className="space-y-8 w-full">
          {/* Welcome — Skiper-style: large display, generous space, subtle eyebrow */}
          <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 pt-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />
                <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground font-medium">
                  Painel principal
                </p>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-[-0.03em] leading-[1.05]">
                Olá, {firstName}
              </h1>
              <p className="text-sm text-muted-foreground max-w-md">
                Bem-vindo ao seu painel de <span className="text-foreground font-medium">poder</span>.
              </p>
            </div>
            <div className="flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-card/70 px-2 py-1">
              {PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPeriod(option.value)}
                  className={
                    option.value === period
                      ? "rounded-md px-2 py-1 text-[11px] text-foreground bg-primary/15"
                      : "rounded-md px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          </header>

          {/* First-run onboarding cards — only when user has zero activity AND incomplete profile */}
          {isFirstRun ? (
            <section aria-labelledby="first-run-heading" className="space-y-3">
              <p
                id="first-run-heading"
                className="text-xs uppercase tracking-[0.18em] text-muted-foreground"
              >
                Comece por aqui ↓
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {FIRST_RUN_CARDS.map((card) => {
                  const Icon = card.icon;
                  return (
                    <Link
                      key={card.to}
                      to={card.to}
                      className="group flex flex-col gap-3 rounded-lg border border-border/60 bg-card/60 p-5 transition-colors hover:border-primary/50 hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      <Icon
                        weight="duotone"
                        aria-hidden
                        className="size-7 text-primary"
                      />
                      <div className="space-y-1.5">
                        <h3 className="text-base font-semibold tracking-tight text-foreground">
                          {card.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-snug">
                          {card.description}
                        </p>
                      </div>
                      <span className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-primary group-hover:gap-2 transition-all">
                        Começar
                        <ArrowRight className="size-4" aria-hidden />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </section>
          ) : null}

          {/* Stats grid — staggered motion entrance */}
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <StaggerItem key={stat.title}>
                <StatCard {...stat} />
              </StaggerItem>
            ))}
          </StaggerContainer>

          <UsageMiniCard />

          {/* Main dashboard section */}
          <StaggerContainer className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <StaggerItem>
              <WeeklyProgress
                title="Progresso de Pontos"
                buckets={buckets}
                totalPoints={periodPoints}
                isLoading={activityLoading}
              />
            </StaggerItem>
            <StaggerItem>
              <RecentActivity />
            </StaggerItem>
          </StaggerContainer>
        </PageTransition>
      </PageStateGate>
    </AppLayout>
  );
}
